import { useMemo } from 'react'
import { reservationWidgetMessages } from '@/reservationWidgetMessages'
import { venuesWithExperiencesSort } from '@/utils'
import type { ReservationWidget } from '@sevenrooms/domain'
import { useLocales } from '@sevenrooms/locales'
import { DateOnly } from '@sevenrooms/timepiece'
import { useMaxWidthBreakpoint } from '@sevenrooms/ui-kit/hooks'
import { Box, VStack } from '@sevenrooms/ui-kit/layout'
import type { ThemeSpacing } from '@sevenrooms/ui-kit/theme'
import { Text } from '@sevenrooms/ui-kit/typography'
import {
  type AvailabilityTimeWithUpSellCost,
  type ExperienceWithAvailability,
  useReservationsRoute,
  useVenue,
  useWidgetLanguage,
  useVenuesExperiencesWithAvailability,
  useOtherVenueNavigation,
} from '../../../../hooks'
import { Experiences, type ExperiencesProps } from '../../Experiences'
import { VenueInfoRow } from '../../VenueInfoRow'
import { PrivateEventsOtherVenuesAvailabilitySkeleton } from './PrivateEventsOtherVenuesAvailabilitySkeleton'
import type { OtherVenueInfo } from '@/domain'

export interface PrivateEventsOtherVenuesAvailabilityProps extends Omit<ExperiencesProps, 'experiences' | 'onTimeSlotClick' | 'startDate'> {
  startDate?: Date
  venuesInfo?: ReservationWidget.VenueInfo
  hideExperiencesWithoutAvailability?: boolean
  onTimeSlotClick: (slot: AvailabilityTimeWithUpSellCost, experience: ExperienceWithAvailability, venue: OtherVenueInfo) => void
  scrollRightMargin?: ThemeSpacing
}

export function PrivateEventsOtherVenuesAvailability({
  isLoading,
  venuesInfo,
  partySize,
  startDate,
  startTime,
  haloTimeIntervalMinutes,
  onTimeSlotClick,
  direction = 'row_preview',
  descriptionType = 'short',
  hideExperiencesWithoutAvailability,
  scrollRightMargin,
  useHorizontalScroll,
  ...props
}: PrivateEventsOtherVenuesAvailabilityProps) {
  const { urlKey: venueUrlKey, startOfDayTime } = useVenue()
  const { formatMessage } = useLocales()
  const { selectedLanguage } = useWidgetLanguage()
  const { clientId } = useReservationsRoute()
  const { navigateToOtherVenue } = useOtherVenueNavigation()
  const isMobile = useMaxWidthBreakpoint('s')

  const otherVenues = useMemo(() => venuesInfo?.venues?.filter(v => v.urlKey !== venueUrlKey), [venueUrlKey, venuesInfo?.venues])
  const venueIds = useMemo(() => otherVenues?.map(({ venueId }) => venueId) || [], [otherVenues])
  const { data: venuesWithExperiences } = useVenuesExperiencesWithAvailability({
    venueIds,
    partySize,
    startDate: startDate ? DateOnly.fromDate(startDate).toIso() : undefined,
    startTime,
    haloTimeIntervalMinutes,
    startOfDayTime,
    clientId,
    selectedLangCode: selectedLanguage,
    experienceType: 'PDR',
    skipRequests: isLoading || !otherVenues?.length,
  })

  const otherVenuesToDisplay = useMemo(() => {
    const venues = venuesWithExperiences?.experiences
    if (!venues || Object.keys(venues || {}).length !== otherVenues?.length) {
      return otherVenues
    }
    const otherVenuesAvailabilityCount = Object.fromEntries(
      Object.entries(venues)?.map(([venueId, experiences]) => [
        venueId,
        experiences.reduce((total, experience) => total + (experience?.reservationTimesInHalo?.length || 0), 0),
      ])
    )
    return venuesWithExperiencesSort(otherVenues, otherVenuesAvailabilityCount)
  }, [otherVenues, venuesWithExperiences?.experiences])

  const isNoAvailability = useMemo(() => {
    if (Object.keys(venuesWithExperiences?.errors || {}).length === otherVenues?.length) {
      return true
    }
    return (
      hideExperiencesWithoutAvailability &&
      Object.values(venuesWithExperiences?.loading || {}).every(isLoading => !isLoading) &&
      Object.values(venuesWithExperiences?.experiences || {}).every(experiences =>
        experiences.every(experience => !experience.reservationTimesInHalo?.length)
      )
    )
  }, [
    venuesWithExperiences?.errors,
    venuesWithExperiences?.loading,
    venuesWithExperiences?.experiences,
    otherVenues?.length,
    hideExperiencesWithoutAvailability,
  ])

  return isLoading ? (
    <VStack spacing="lm">
      <PrivateEventsOtherVenuesAvailabilitySkeleton desktopVariant={props.desktopVariant} />
    </VStack>
  ) : (
    <>
      {isNoAvailability ? (
        <Box>
          <Text textStyle="body1" data-test="other-venues-no-availability-text">
            {formatMessage(reservationWidgetMessages.resWidgetNoAvailabilityCriteria, {
              optionalLink: () => null,
            })}
          </Text>
        </Box>
      ) : (
        <VStack spacing="xxl">
          {otherVenuesToDisplay?.map(otherVenue => {
            const { venueId: otherVenueId } = otherVenue
            let venueExperiences = venuesWithExperiences?.experiences?.[otherVenueId]
            const isLoading = venuesWithExperiences?.loading?.[otherVenueId]
            const isError = !!venuesWithExperiences?.errors?.[otherVenueId]
            if (!isLoading) {
              if (!venueExperiences?.length) {
                return null
              }
              if (hideExperiencesWithoutAvailability) {
                venueExperiences = venueExperiences?.filter(experience => experience.reservationTimesInHalo?.length)
                if (!venueExperiences?.length) {
                  return null
                }
              }
            }
            return (
              <Experiences
                {...props}
                key={otherVenueId}
                label={<VenueInfoRow venueInfo={otherVenue} onTitleClick={navigateToOtherVenue} />}
                partySize={partySize}
                startDate={startDate ? DateOnly.fromDate(startDate).toIso() : undefined}
                startTime={startTime}
                haloTimeIntervalMinutes={haloTimeIntervalMinutes}
                experiences={venueExperiences}
                otherVenue={otherVenue}
                isLoading={isLoading}
                isError={isError}
                onTimeSlotClick={(slot: AvailabilityTimeWithUpSellCost, experience: ExperienceWithAvailability) =>
                  onTimeSlotClick(slot, experience, otherVenue)
                }
                descriptionType={descriptionType}
                direction={direction}
                onShowMoreClick={() => navigateToOtherVenue(otherVenue.urlKey)}
                isPrivateEvents
                scrollRightMargin={scrollRightMargin}
                useHorizontalScroll={useHorizontalScroll && !isMobile && (venueExperiences?.length || 0) > 2}
              />
            )
          })}
        </VStack>
      )}
    </>
  )
}
